import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const FitnessPreferencesSection = ({ preferences, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    workoutTypes: preferences?.workoutTypes,
    difficultyLevel: preferences?.difficultyLevel,
    workoutDuration: preferences?.workoutDuration,
    fitnessGoals: preferences?.fitnessGoals,
    preferredDays: preferences?.preferredDays,
    preferredTime: preferences?.preferredTime
  });

  const workoutTypeOptions = [
    { value: 'strength', label: 'Strength Training' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'hiit', label: 'HIIT' },
    { value: 'pilates', label: 'Pilates' },
    { value: 'crossfit', label: 'CrossFit' }
  ];

  const difficultyOptions = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const durationOptions = [
    { value: '15-30', label: '15-30 minutes' },
    { value: '30-45', label: '30-45 minutes' },
    { value: '45-60', label: '45-60 minutes' },
    { value: '60+', label: '60+ minutes' }
  ];

  const timeOptions = [
    { value: 'morning', label: 'Morning (5AM - 11AM)' },
    { value: 'afternoon', label: 'Afternoon (11AM - 5PM)' },
    { value: 'evening', label: 'Evening (5PM - 9PM)' },
    { value: 'night', label: 'Night (9PM - 12AM)' }
  ];

  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      workoutTypes: preferences?.workoutTypes,
      difficultyLevel: preferences?.difficultyLevel,
      workoutDuration: preferences?.workoutDuration,
      fitnessGoals: preferences?.fitnessGoals,
      preferredDays: preferences?.preferredDays,
      preferredTime: preferences?.preferredTime
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg">
            <Icon name="Target" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Fitness Preferences
            </h2>
            <p className="text-sm text-muted-foreground caption">
              Customize your workout experience
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Preferred Workout Types
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {workoutTypeOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                checked={formData?.workoutTypes?.includes(option?.value)}
                onChange={(e) => {
                  if (e?.target?.checked) {
                    setFormData({
                      ...formData,
                      workoutTypes: [...formData?.workoutTypes, option?.value]
                    });
                  } else {
                    setFormData({
                      ...formData,
                      workoutTypes: formData?.workoutTypes?.filter(t => t !== option?.value)
                    });
                  }
                }}
                disabled={!isEditing}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Difficulty Level"
            options={difficultyOptions}
            value={formData?.difficultyLevel}
            onChange={(value) => setFormData({ ...formData, difficultyLevel: value })}
            disabled={!isEditing}
          />
          <Select
            label="Preferred Workout Duration"
            options={durationOptions}
            value={formData?.workoutDuration}
            onChange={(value) => setFormData({ ...formData, workoutDuration: value })}
            disabled={!isEditing}
          />
          <Select
            label="Preferred Time"
            options={timeOptions}
            value={formData?.preferredTime}
            onChange={(value) => setFormData({ ...formData, preferredTime: value })}
            disabled={!isEditing}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Fitness Goals
          </label>
          <div className="flex flex-wrap gap-2">
            {formData?.fitnessGoals?.map((goal, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full"
              >
                <Icon name="Target" size={16} />
                <span className="text-sm font-medium caption">{goal}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isEditing && (
        <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border">
          <Button
            variant="outline"
            size="default"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default FitnessPreferencesSection;